import { Fragment, type ReactNode, useEffect, useState } from "react";
import { Link, useLocation } from "react-router";
import { ChevronsUpDown } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useOptionalAccountWorkspace } from "@/lib/account-workspace";
import { KB_COPY } from "@/lib/kb-copy";
import { defaultAppSection, enabledBrandModules } from "@/lib/modules";
import { fetchBrandSites, type BrandSite } from "@/lib/brand-site-api";
import {
  brandOperatingToolPath,
  brandSiteListPath,
  brandSitePath,
  isBrandSiteSection,
  type BrandSiteSection,
} from "@/lib/brand-site-routing";
import { useShellRouteInfo } from "@/lib/shell-navigation";
import { SHELL_HEADER_CLASS } from "./shell-header";

const ADMIN_SECTION_NAMES: Record<string, string> = {
  brands: "品牌管理",
  members: "成员",
  authorizations: "代管授权",
  "received-authorizations": "代管授权",
  settings: "账户设置",
  subscriptions: "订阅",
  wecom: "企微接入",
  channels: "渠道",
  kb: "企业知识库",
  inbox: "企业知识旅程",
  materials: "素材库",
  "audit-log": "审计日志",
  assignees: "可进入人员",
};

const KB_SUBSECTION_NAMES: Record<string, string> = {
  audit: "审计 / 版本",
};

const BRAND_CONTENT_SECTION_NAMES: Record<string, string> = {
  kb: "品牌知识库",
  inbox: "品牌知识旅程",
  materials: "素材库",
  plan: "运营计划",
  brand_site: "AI 官网",
  connectors: "连接器",
};

function IdentityLabel({ children }: { children: ReactNode }) {
  return <span className="identity-mark inline-flex items-center leading-none">{children}</span>;
}

function ToolCrumb({
  accountId,
  brandId,
  appKey,
  appName,
}: {
  accountId: string;
  brandId: string;
  appKey: string;
  appName: string;
}) {
  const workspace = useOptionalAccountWorkspace();
  const modules = workspace?.modules ?? [];
  const brand = workspace?.brands.find((item) => String(item.id) === brandId);
  const switchable = enabledBrandModules(modules, brand);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-heading text-sm font-medium outline-none hover:bg-accent">
        {appName}
        <ChevronsUpDown className="size-3.5 opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-48">
        <DropdownMenuLabel className="text-xs text-muted-foreground">切换工具</DropdownMenuLabel>
        {switchable.length === 0 ? (
          <DropdownMenuItem disabled className="opacity-50">
            本品牌暂无已开通工具
          </DropdownMenuItem>
        ) : (
          switchable.map((app) => (
            <DropdownMenuItem key={app.key} asChild className="cursor-pointer">
              <Link
                to={brandOperatingToolPath(accountId, brandId, app.key, defaultAppSection(app))}
              >
                <span className="min-w-0 flex-1 truncate font-heading">{app.name}</span>
                {app.key === appKey ? <span className="text-xs text-muted-foreground">当前</span> : null}
              </Link>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function BrandSiteCrumb({
  accountId,
  brandId,
  siteId,
  section,
}: {
  accountId: string;
  brandId: string;
  siteId: string;
  section: BrandSiteSection;
}) {
  const [sites, setSites] = useState<BrandSite[]>([]);

  useEffect(() => {
    let cancelled = false;
    void fetchBrandSites(accountId, brandId)
      .then((payload) => {
        if (!cancelled) setSites(payload.brand_sites ?? []);
      })
      .catch(() => {
        if (!cancelled) setSites([]);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, brandId]);

  const current = sites.find((site) => String(site.id) === String(siteId));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="inline-flex max-w-48 items-center gap-1 rounded-md px-1.5 py-0.5 font-heading text-sm font-medium outline-none hover:bg-accent"
        aria-label="切换官网"
      >
        <span className="truncate">{current?.title ?? "官网"}</span>
        <ChevronsUpDown className="size-3.5 shrink-0 opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-48">
        <DropdownMenuLabel className="text-xs text-muted-foreground">切换官网</DropdownMenuLabel>
        <DropdownMenuItem asChild className="cursor-pointer">
          <Link to={brandSiteListPath(accountId, brandId)}>全部官网</Link>
        </DropdownMenuItem>
        {sites.map((site) => (
          <DropdownMenuItem key={site.id} asChild className="cursor-pointer">
            <Link to={brandSitePath(accountId, brandId, site.id, section)}>
              <span className="min-w-0 flex-1 truncate">{site.title}</span>
              {String(site.id) === String(siteId) ? (
                <span className="text-xs text-muted-foreground">当前</span>
              ) : null}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function SiteHeader() {
  const { pathname } = useLocation();
  const workspace = useOptionalAccountWorkspace();
  const shell = useShellRouteInfo();
  const accountName = workspace?.account?.name ?? "当前账户";
  const accountId = shell.accountId;
  const brand = workspace?.brands.find((item) => String(item.id) === shell.brandId) ?? null;

  const crumbs: { label: React.ReactNode; to?: string }[] = [];

  if (!accountId) {
    crumbs.push({ label: <IdentityLabel>工作台</IdentityLabel> });
  } else if (shell.mode === "product" && shell.brandId && shell.appKey) {
    crumbs.push({ label: <IdentityLabel>{accountName}</IdentityLabel>, to: `/accounts/${accountId}` });
    crumbs.push({
      label: brand?.name ?? `品牌 #${shell.brandId}`,
      to: `/accounts/${accountId}/brands/${shell.brandId}`,
    });
    crumbs.push({
      label: (
        <ToolCrumb
          accountId={accountId}
          brandId={shell.brandId}
          appKey={shell.appKey}
          appName={shell.appName ?? shell.appKey}
        />
      ),
    });
    if (shell.appKey === "brand_site" && shell.brandSiteId) {
      crumbs.push({
        label: (
          <BrandSiteCrumb
            accountId={accountId}
            brandId={shell.brandId}
            siteId={shell.brandSiteId}
            section={isBrandSiteSection(shell.appSection) ? shell.appSection : "overview"}
          />
        ),
      });
      if (shell.appSection === "materials") {
        crumbs.push({ label: "站点素材库" });
      }
    }
  } else if (shell.mode === "brand" && shell.brandId) {
    crumbs.push({ label: <IdentityLabel>{accountName}</IdentityLabel>, to: `/accounts/${accountId}` });
    const brandSection = pathname.split("/")[5] ?? "";
    const brandHasChild =
      brandSection === "kb" ||
      brandSection === "inbox" ||
      brandSection === "materials" ||
      brandSection === "plan" ||
      brandSection === "brand_site";
    crumbs.push({
      label: brand?.name ?? `品牌 #${shell.brandId}`,
      to: brandHasChild ? `/accounts/${accountId}/brands/${shell.brandId}` : undefined,
    });
    if (brandHasChild) {
      const sectionRoot = `/accounts/${accountId}/brands/${shell.brandId}/${brandSection}`;
      crumbs.push({
        label: BRAND_CONTENT_SECTION_NAMES[brandSection] ?? brandSection,
        to: brandSection === "kb" && pathname.includes("/wikis/") ? sectionRoot : undefined,
      });
      if (brandSection === "kb" && pathname.includes("/wikis/")) {
        crumbs.push({ label: KB_COPY.breadcrumbEdit });
      }
    }
  } else if (shell.mode === "enterprise") {
    crumbs.push({ label: <IdentityLabel>{accountName}</IdentityLabel>, to: `/accounts/${accountId}` });
    crumbs.push({ label: "企业画像" });
  } else if (shell.mode === "account-admin") {
    crumbs.push({ label: <IdentityLabel>{accountName}</IdentityLabel>, to: `/accounts/${accountId}` });
    if (pathname.includes("received-authorizations") && pathname.includes("/assignees")) {
      crumbs.push({
        label: "代管授权",
        to: `/accounts/${accountId}/authorizations?tab=received`,
      });
      crumbs.push({ label: "可进入人员" });
    } else {
      const section = pathname.split("/")[3] ?? "";
      if (section === "kb") {
        crumbs.push({ label: "企业知识库", to: `/accounts/${accountId}/kb` });
        const kbSub = pathname.split("/")[4] ?? "";
        if (kbSub === "audit") {
          crumbs.push({ label: KB_SUBSECTION_NAMES.audit });
        } else if (kbSub === "wikis") {
          crumbs.push({ label: KB_COPY.breadcrumbEdit });
        }
      } else {
        crumbs.push({ label: ADMIN_SECTION_NAMES[section] || section || "账户管理" });
      }
    }
  } else {
    crumbs.push({ label: <IdentityLabel>{accountName || "工作台"}</IdentityLabel> });
  }

  return (
    <header
      className={cn(
        SHELL_HEADER_CLASS,
        "sticky top-0 z-10 gap-2 bg-background transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12",
      )}
    >
      <div className="flex w-full min-w-0 items-center gap-1 px-4 lg:gap-2 lg:px-6">
        <SidebarTrigger className="-ml-1 shrink-0 cursor-pointer" />
        <Separator
          orientation="vertical"
          className="mx-2 h-4 shrink-0 self-center data-[orientation=vertical]:self-center"
        />
        <Breadcrumb className="min-w-0">
          <BreadcrumbList className="flex-nowrap leading-none">
            {crumbs.map((crumb, index) => {
              const isLast = index === crumbs.length - 1;
              return (
                <Fragment key={index}>
                  {index > 0 && (
                    <BreadcrumbSeparator className="hidden items-center md:flex" />
                  )}
                  <BreadcrumbItem>
                    {isLast || !crumb.to ? (
                      <BreadcrumbPage className="inline-flex items-center leading-none">
                        {crumb.label}
                      </BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink asChild className="inline-flex items-center leading-none">
                        <Link to={crumb.to}>{crumb.label}</Link>
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                </Fragment>
              );
            })}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
    </header>
  );
}
